// Admin → Bildirim → Promosyon push: toplu bildirim gonderim sayfasi.
// Filtreye uyan musterileri BullMQ kuyruguna ekler, worker async gonderim yapar.

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Send, Users, CheckCircle2, AlertCircle, Clock, Hourglass } from 'lucide-react';
import { Buton, Etiket, Girdi, Iskelet, Kart, KartIcerik, toast } from '@emanetx/ui';
import {
  promosyonPushGonder,
  promosyonPushIstatistik,
  type PromosyonPushFiltresi,
} from '@emanetx/sdk';
import { istemci } from '../istemci.js';

interface Form {
  dil: '' | 'tr' | 'en';
  sonGirisGunIcinde: string;
  baslik: string;
  govde: string;
}

const BOS_FORM: Form = {
  dil: '',
  sonGirisGunIcinde: '',
  baslik: '',
  govde: '',
};

export function SayfaPromosyon() {
  const [form, setForm] = useState<Form>(BOS_FORM);

  const istatistikSorgu = useQuery({
    queryKey: ['bildirim', 'promosyon', 'istatistik'],
    queryFn: () => promosyonPushIstatistik(istemci),
    refetchInterval: 5_000,
  });

  const gonder = useMutation({
    mutationFn: () => {
      const filtre: PromosyonPushFiltresi = {};
      if (form.dil) filtre.dil = form.dil;
      if (form.sonGirisGunIcinde) {
        const gun = Number(form.sonGirisGunIcinde);
        if (gun > 0) filtre.sonGirisGunIcinde = gun;
      }
      return promosyonPushGonder(istemci, {
        filtre,
        baslik: form.baslik.trim(),
        govde: form.govde.trim(),
      });
    },
    onSuccess: (sonuc) => {
      toast.success(`${sonuc.hedefSayisi} müşteri kuyruğa eklendi — worker async gönderecek`);
      setForm((s) => ({ ...s, baslik: '', govde: '' }));
    },
    onError: (err: Error) => toast.error(`Gönderim başarısız: ${err.message}`),
  });

  const gecerli =
    form.baslik.trim().length >= 2 && form.govde.trim().length >= 2 && !gonder.isPending;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-fume-900 dark:text-fume-50 text-2xl font-extrabold tracking-tight">
          Promosyon Push
        </h1>
        <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
          Filtreye uyan müşterilere toplu push bildirim gönder. Worker kuyruktan async işler.
        </p>
      </header>

      {/* Kuyruk istatistikleri */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Istatistik
          ikon={Hourglass}
          etiket="Bekliyor"
          deger={istatistikSorgu.data?.bekliyor}
          ton="bekleme"
        />
        <Istatistik
          ikon={Clock}
          etiket="Geciken"
          deger={istatistikSorgu.data?.geciken}
          ton="bekleme"
        />
        <Istatistik ikon={Send} etiket="Aktif" deger={istatistikSorgu.data?.aktif} ton="aktif" />
        <Istatistik
          ikon={CheckCircle2}
          etiket="Tamamlandı"
          deger={istatistikSorgu.data?.tamamlandi}
          ton="basari"
        />
        <Istatistik
          ikon={AlertCircle}
          etiket="Başarısız"
          deger={istatistikSorgu.data?.basarisiz}
          ton="hata"
        />
      </div>

      {/* Gonderim formu */}
      <Kart>
        <KartIcerik className="space-y-5 p-6">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-extrabold">
              <Users size={18} />
              Hedef Filtre
            </h2>
            <p className="text-fume-500 mt-1 text-xs">
              Boş bırakılan alan = tüm aktif müşteriler (push token kayıtlı olanlar).
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Etiket htmlFor="dil">Dil tercihi</Etiket>
              <select
                id="dil"
                value={form.dil}
                onChange={(e) => setForm((s) => ({ ...s, dil: e.target.value as Form['dil'] }))}
                className="border-fume-200 dark:border-fume-700 bg-fume-50 dark:bg-fume-900 focus:border-marka-500 mt-1 w-full rounded-lg border px-3 py-2 text-sm font-medium outline-none"
              >
                <option value="">Hepsi</option>
                <option value="tr">Türkçe</option>
                <option value="en">English</option>
              </select>
            </div>
            <div>
              <Etiket htmlFor="sonGiris">Son giriş (gün içinde)</Etiket>
              <Girdi
                id="sonGiris"
                type="number"
                min={1}
                max={365}
                placeholder="Örn. 30 (son 30 gün)"
                value={form.sonGirisGunIcinde}
                onChange={(e) => setForm((s) => ({ ...s, sonGirisGunIcinde: e.target.value }))}
              />
            </div>
          </div>

          <hr className="border-fume-200 dark:border-fume-700" />

          <div>
            <h2 className="text-lg font-extrabold">Mesaj</h2>
            <p className="text-fume-500 mt-1 text-xs">
              Başlık kısa olsun (50 karakter altı önerilir). Gövde 2-3 cümle ideal.
            </p>
          </div>

          <div>
            <Etiket htmlFor="baslik">Başlık</Etiket>
            <Girdi
              id="baslik"
              placeholder="Bugüne özel %20 indirim!"
              maxLength={120}
              value={form.baslik}
              onChange={(e) => setForm((s) => ({ ...s, baslik: e.target.value }))}
            />
            <p className="text-fume-400 mt-1 text-right text-xs">{form.baslik.length}/120</p>
          </div>

          <div>
            <Etiket htmlFor="govde">Gövde</Etiket>
            <textarea
              id="govde"
              placeholder="Tüm dolaplarda bugün geçerli. Uygulamayı aç, kuponunu kullan."
              maxLength={500}
              rows={4}
              value={form.govde}
              onChange={(e) => setForm((s) => ({ ...s, govde: e.target.value }))}
              className="border-fume-200 dark:border-fume-700 bg-fume-50 dark:bg-fume-900 focus:border-marka-500 mt-1 w-full rounded-lg border px-3 py-2 text-sm font-medium outline-none"
            />
            <p className="text-fume-400 mt-1 text-right text-xs">{form.govde.length}/500</p>
          </div>

          <div className="flex justify-end">
            <Buton onClick={() => gonder.mutate()} disabled={!gecerli} className="gap-2">
              <Send size={16} />
              {gonder.isPending ? 'Gönderiliyor...' : 'Kuyruğa Ekle'}
            </Buton>
          </div>
        </KartIcerik>
      </Kart>

      {istatistikSorgu.isLoading && <Iskelet className="h-16" />}
    </div>
  );
}

function Istatistik({
  ikon: Ikon,
  etiket,
  deger,
  ton,
}: {
  ikon: React.ComponentType<{ size?: number; className?: string }>;
  etiket: string;
  deger: number | undefined;
  ton: 'bekleme' | 'aktif' | 'basari' | 'hata';
}) {
  const tonSinif = {
    bekleme: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10',
    aktif: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10',
    basari: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10',
    hata: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10',
  }[ton];
  return (
    <Kart>
      <KartIcerik className="p-4">
        <div className="flex items-center gap-3">
          <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tonSinif}`}>
            <Ikon size={18} />
          </div>
          <div>
            <p className="text-fume-500 text-xs">{etiket}</p>
            <p className="text-fume-900 dark:text-fume-50 font-mono text-xl font-extrabold">
              {deger ?? '—'}
            </p>
          </div>
        </div>
      </KartIcerik>
    </Kart>
  );
}
